import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, Card, CardContent, Container, Typography } from '@mui/material';
import { FC } from 'react';
import Show from '../Common/show';
import { useInView } from '../../hooks/useInView';

const PRIMARY = '#8c7c0c';

const basicFeatures = [
    'Listing properties',
    'Rent collection',
    'Record keeping',
    'Tenant Reminder Notifications',
    'Tenant Screening and Onboarding',
];

const silverPlan = [
    'Debt recovery',
    'Maintenance',
    'Caretaking',
    'Sheltergram is the custodian of tenant deposit.',
];

const allFeatures = [
    ...basicFeatures,
    'Payments are remitted immediately when the tenant pays',
    ...silverPlan,
    'Total payments are made on 5th of every month regardless of whether tenants have paid or not',
];

const pricingPlans = [
    {
        title: 'Bronze Lite',
        price: '2.5%',
        description: 'Landlord manages the property themselves using the platform.',
        background: 'linear-gradient(135deg, #f5e1c0 0%, #c8a777 100%)',
        includedFeatures: ['Rent collection', 'Record keeping', 'Tenant Reminder Notifications'],
    },
    {
        title: 'Bronze',
        price: '5%',
        description: 'Landlord manages the property themselves using the platform.',
        background: 'linear-gradient(135deg, #e0b07e 0%, #a66a3c 100%)',
        includedFeatures: [...basicFeatures, 'Payments are remitted immediately when the tenant pays'],
    },
    {
        title: 'Silver',
        price: '7.5%',
        description: 'Sheltergram handles the entire management.',
        background: 'linear-gradient(135deg, #dcdcdc 0%, #a8a8a8 100%)',
        includedFeatures: [...basicFeatures, 'Payments are remitted immediately when the tenant pays', ...silverPlan],
    },
    {
        title: 'Gold',
        price: '10%',
        description: 'Sheltergram handles the entire management.',
        background: 'linear-gradient(135deg, #ffd700 0%, #b8860b 100%)',
        includedFeatures: [
            ...basicFeatures,
            ...silverPlan,
            'Total payments are made on 5th of every month regardless of whether tenants have paid or not',
        ],
    },
];

const Plans: FC = () => {
    const { ref, visible } = useInView();

    return (
        <Box
            component="section"
            id="plans"
            ref={ref}
            sx={{
                py: { xs: 8, md: 10 },
                background: 'linear-gradient(135deg, #f9f9f0 0%, #c1c196 100%)',
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 6 }} className={`sg-reveal ${visible ? 'sg-visible' : ''}`}>
                    <Typography component="h2" sx={{ fontSize: { xs: 28, md: 38 }, fontWeight: 800, color: '#111827', mb: 1 }}>
                        Pricing Plans
                    </Typography>
                    <Typography sx={{ fontSize: 16, color: '#6b7280', maxWidth: 520, mx: 'auto' }}>
                        Simple, transparent pricing based on rent collected. No hidden fees.
                    </Typography>
                </Box>

                {/* Horizontally scrollable on mobile, grid on desktop */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        overflowX: 'auto',
                        pb: 2,
                        '&::-webkit-scrollbar': { height: 4, bgcolor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: `${PRIMARY}40`, borderRadius: 2 },
                        scrollbarWidth: 'thin',
                    }}
                >
                    {pricingPlans.map((plan, i) => (
                        <Card
                            key={plan.title}
                            className={`sg-reveal sg-card-lift ${visible ? 'sg-visible' : ''}`}
                            style={{ transitionDelay: `${i * 0.1}s` }}
                            sx={{
                                minWidth: 270,
                                flex: '0 0 270px',
                                borderRadius: 3,
                                overflow: 'hidden',
                                border: '1px solid #e5e7eb',
                                boxShadow: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box sx={{ background: plan.background, py: 1.5, textAlign: 'center' }}>
                                <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{plan.title}</Typography>
                            </Box>
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                                <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                                    <Typography sx={{ fontSize: 36, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{plan.price}</Typography>
                                    <Typography sx={{ fontSize: 13, color: '#9ca3af', mt: 0.3 }}>of paid rent</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 13, color: '#6b7280', textAlign: 'center', mb: 2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {plan.description}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                                    {allFeatures.map((feature) => (
                                        <Box key={feature} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Show>
                                                <Show.When isTrue={plan.includedFeatures.includes(feature)}>
                                                    <CheckCircle sx={{ fontSize: 18, color: PRIMARY, flexShrink: 0, mt: 0.2 }} />
                                                </Show.When>
                                                <Show.Else>
                                                    <Cancel sx={{ fontSize: 18, color: '#d1d5db', flexShrink: 0, mt: 0.2 }} />
                                                </Show.Else>
                                            </Show>
                                            <Typography sx={{ fontSize: 13, color: plan.includedFeatures.includes(feature) ? '#374151' : '#9ca3af', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5 }}>
                                                {feature}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

export default Plans;
